import { JsonFormsCore, JsonSchema } from '@jsonforms/core'
import * as Sentry from '@sentry/react'
import { Dispatch, SetStateAction, useState } from 'react'
import { getGenericClient } from '../../../../client'
import { Button, Copy } from '../../../../elements'
import { config } from '../../../../shared'
import { JsonForm } from '../../../json-forms/components'
import { useAsync } from 'react-use'
import { Callout } from '../../../../elementsv2'

type IntegrationFormProps = {
  webhookId?: string
  iconUrl?: string
  identifier?: string
  identifierRequired?: boolean
  linkTemplateScript?: string
  setData: Dispatch<SetStateAction<Record<string, any>>>
  setErrors?: (errors: any) => void
  schema?: JsonSchema
  readonly?: boolean
  data: Record<string, any>
}

export const IntegrationForm = ({
  webhookId,
  linkTemplateScript,
  iconUrl,
  setErrors,
  setData,
  identifier,
  identifierRequired,
  data,
  readonly,
  schema,
}: IntegrationFormProps) => {
  const [authorizationUrl, setAuthorizationUrl] = useState('')
  const [error, setError] = useState('')

  useAsync(async () => {
    if (webhookId && linkTemplateScript) {
      const client = getGenericClient()
      try {
        const { result } = await client.runVrl({
          script: linkTemplateScript,
          data: {
            env: config.appEnv,
            webhookId,
            webhookUrl: config.webhookBaseUrl,
          },
        })

        if (typeof result === 'string') {
          setAuthorizationUrl(result) // Link template script returns a plain string rather than an object.
          setError('')
        }
      } catch (e: any) {
        setError('Error rendering button for authorization. Please reload the page.')
        Sentry.captureException(e, { tags: { message: 'Error executing link template', linkTemplateScript } })
      }
    }
  }, [webhookId, linkTemplateScript])

  return (
    <div className="grid gap-2">
      {error && (
        <Callout color="red" className="mb-8">
          {error}
        </Callout>
      )}
      {authorizationUrl && (
        <Button
          className="mb-8 flex items-center justify-between rounded-md border-accent-9 bg-accent-9 p-[1px] text-white hover:border-accent-10 hover:bg-accent-10 hover:shadow-md"
          onClick={() => window.open(authorizationUrl, 'OAuth', 'width=650,height=800,popup=true')}
        >
          <div className="m-px flex h-12 w-12 items-center justify-center rounded-l-md bg-gray-1">
            <img className="h-5 w-5" src={iconUrl} alt="Logo" />
          </div>
          <div className="spacing font-semibold text-accent-1">Sign in to link your account</div>
          <div />
        </Button>
      )}
      {webhookId && !identifierRequired && (
        <Copy
          className="mb-8"
          label="Webhook URL"
          value={`${config.webhookBaseUrl}/${webhookId}`}
          description="Webhook URL"
        />
      )}
      {(identifier || !identifierRequired) && (
        <JsonForm
          data={data}
          readonly={readonly}
          schema={schema}
          onChange={({ data: newData, errors }: Pick<JsonFormsCore, 'data' | 'errors'>) => {
            setErrors?.(errors)
            setData?.(newData)
          }}
        />
      )}
    </div>
  )
}
